/* eslint-disable no-param-reassign,default-case */
import { namespace, EventEmitter } from '../../../../../FrontendBundle/Resources/js/ofcs';
import Guide from './guide';

/**
 * @extends EventEmitter
 */
export default class GuideManager extends EventEmitter {

    /**
     * Triggered when guides are enabled
     *
     * @event enable
     * @param {GuideManager} guideManager Me
     */

    /**
     * Triggered when guides are disabled
     *
     * @event disable
     * @param {GuideManager} guideManager Me
     */

    /**
     * Triggered when check starts
     *
     * @event start
     * @param {GuideManager} guideManager Me
     */

    /**
     * Triggered when check stops
     *
     * @event stop
     * @param {GuideManager} guideManager Me
     */

    /**
     * Triggered when check is in progress
     *
     * @event check
     * @param {GuideManager} guideManager Me
     */

    /**
     * Triggered when a guide has been found
     *
     * @event guide
     * @param {GuideManager} guideManager Me
     * @param {Guide} guide Guide
     */

    /**
     * Triggered when a snap has been found
     *
     * @event snap
     * @param {GuideManager} guideManager Me
     * @param {Guide} guide Guide
     */

    /**
     * Initializes the guide manager
     *
     * @param {Ofcs.Editor.Svg.Tool.SelectTool} selectTool Select tool
     * @param {Ofcs.Editor.Context.ContextManager} contextManager Context Manager
     * @param {number} tolerance Snap tolerance
     * @param {boolean} isActive is guide manager activated (feature flag)
     */
    constructor(selectTool, contextManager, tolerance, isActive) {
        super();

        this.selectTool = selectTool;
        this.contextManager = contextManager;
        this.tolerance = tolerance !== undefined ? tolerance : 5;
        this.isActive = isActive;

        selectTool.on('startmove', this.start, this);
        selectTool.on('move', this.check, this);
        selectTool.on('finishmove', this.stop, this);

        if (isActive) this.enable();
        else this.disable();
    }

    /**
     * Enable guides
     */
    enable() {
        if (!this.isActive) return;
        this.enabled = true;

        this.trigger('enable', this);
    }

    /**
     * Disable guides
     */
    disable() {
        if (!this.isActive) return;
        this.enabled = false;

        this.trigger('disable', this);
    }

    /**
     * Toggle guides
     */
    toggle() {
        if (this.enabled) this.disable();
        else this.enable();
    }

    /**
     * Start guide check, store selection bounds
     * If nothing is selected, do nothing
     *
     * @param {Ofcs.Editor.Svg.Tool.SelectTool} selectTool
     * @param {Ofcs.Editor.Canvas.SelectionCollection} selection
     */
    start(selectTool, selection) {
        delete this.startBounds;

        if (!this.enabled || !selection.length || !this.checkGuideSupport(selection)) return;

        this.startBounds = selection.getAbsoluteBounds();

        this.trigger('start', this);
    }

    /**
     * checks if a given selection contains at least
     * one item which supports guides
     *
     * @param {Ofcs.Editor.Canvas.SelectionCollection} selection
     * @return {boolean}
     */
    checkGuideSupport(selection) {
        return !!selection.find(item => item.supportsGuides);
    }

    /**
     * Stop guides check
     */
    stop() {
        delete this.startBounds;

        this.trigger('stop', this);
    }

    /**
     * Check for guides and snap
     *
     * @param {Ofcs.Editor.Svg.Tool.SelectTool} selectTool
     * @param {Ofcs.Editor.Canvas.SelectionCollection} selection
     * @param {Distance} distance
     */
    check(selectTool, selection, distance) {
        this.trigger('check', this);

        if (!this.enabled || !this.startBounds) return;

        const guides = this.createGuides(distance, selection);
        const { snapX, snapY } = this.snap(distance, selection, guides);

        this.triggerGuides(distance, selection, guides);

        if (snapX) this.trigger('snap', this, snapX);
        if (snapY) this.trigger('snap', this, snapY);
    }

    /**
     * @param {Distance} distance
     * @param {Ofcs.Editor.Canvas.SelectionCollection} selection
     * @param {Object} guides
     */
    triggerGuides(distance, selection, guides) {
        guides.forEach((guide) => {
            if (guide.getDistance() < 0.001) this.trigger('guide', this, guide);
        });
    }

    /**
     * @param {Distance} distance
     * @param {Ofcs.Editor.Canvas.SelectionCollection} selection
     * @param {Array} guides
     * @return {{snapX: Guide, snapY: Guide}}
     */
    snap(distance, selection, guides) {
        let snapX = new Guide('dummy', '', 0, 9999);
        let snapY = new Guide('dummy', '', 0, 9999);
        const winners = {};

        guides.forEach((guide) => {
            if (guide.getOrientation() === 'x' && guide.getDistance() < snapX.getDistance()) {
                snapX = guide;
            } else if (guide.getOrientation() === 'y' && guide.getDistance() < snapY.getDistance()) {
                snapY = guide;
            }
        });

        if (snapX.getOrientation() !== 'dummy') {
            switch (snapX.getHit()) {
                case 'left':
                    distance.point.x = snapX.getPosition() - this.startBounds.topLeft.x;
                    break;

                case 'right':
                    distance.point.x = snapX.getPosition() - this.startBounds.bottomRight.x;
                    break;

                case 'center':
                    distance.point.x = snapX.getPosition() - this.startBounds.getCenter().x;
                    break;
            }

            winners.snapX = snapX;
        }

        if (snapY.getOrientation() !== 'dummy') {
            switch (snapY.getHit()) {
                case 'top':
                    distance.point.y = snapY.getPosition() - this.startBounds.topLeft.y;
                    break;

                case 'bottom':
                    distance.point.y = snapY.getPosition() - this.startBounds.bottomRight.y;
                    break;

                case 'center':
                    distance.point.y = snapY.getPosition() - this.startBounds.getCenter().y;
                    break;
            }

            winners.snapY = snapY;
        }

        return winners;
    }

    /**
     * @param {Distance} distance
     * @param {Ofcs.Editor.Canvas.SelectionCollection} selection
     * @return {Array}
     */
    createGuides(distance, selection) {
        const items = this.contextManager.getCurrentItems();
        const bounds = this.startBounds.clone().move(distance);
        const { x: left, y: top } = bounds.getTopLeft();
        const { x: right, y: bottom } = bounds.getBottomRight();
        const { x: centerX, y: centerY } = bounds.getCenter();
        const guides = [];

        items.each((item) => {
            if (!item.supportsGuides || selection.get(item.cid)) return;

            const itemBounds = item.getAbsoluteBounds();
            const { x: itemLeft, y: itemTop } = itemBounds.getTopLeft();
            const { x: itemRight, y: itemBottom } = itemBounds.getBottomRight();
            const { x: itemCenterX, y: itemCenterY } = itemBounds.getCenter();
            const distanceCenterX = Math.abs(itemCenterX - centerX);
            const distanceLeftLeft = Math.abs(itemLeft - left);
            const distanceLeftRight = Math.abs(itemRight - left);
            const distanceRightLeft = Math.abs(itemLeft - right);
            const distanceRightRight = Math.abs(itemRight - right);
            const distanceCenterY = Math.abs(itemCenterY - centerY);
            const distanceTopTop = Math.abs(itemTop - top);
            const distanceTopBottom = Math.abs(itemBottom - top);
            const distanceBottomTop = Math.abs(itemTop - bottom);
            const distanceBottomBottom = Math.abs(itemBottom - bottom);

            // selection centerX snaps to item centerX
            if (distanceCenterX < this.tolerance) {
                guides.push(new Guide('x', 'center', itemCenterX, distanceCenterX));
            }
            // selection left snaps to item left
            if (distanceLeftLeft < this.tolerance) {
                guides.push(new Guide('x', 'left', itemLeft, distanceLeftLeft));
            }
            // selection right snaps to item left
            if (distanceRightLeft < this.tolerance) {
                guides.push(new Guide('x', 'right', itemLeft, distanceRightLeft));
            }
            // selection left snaps to item right
            if (distanceLeftRight < this.tolerance) {
                guides.push(new Guide('x', 'left', itemRight, distanceLeftRight));
            }
            // selection right snaps to item right
            if (distanceRightRight < this.tolerance) {
                guides.push(new Guide('x', 'right', itemRight, distanceRightRight));
            }

            // selection centerY snaps to item centerY
            if (distanceCenterY < this.tolerance) {
                guides.push(new Guide('y', 'center', itemCenterY, distanceCenterY));
            }
            // selection top snaps to item top
            if (distanceTopTop < this.tolerance) {
                guides.push(new Guide('y', 'top', itemTop, distanceTopTop));
            }
            // selection bottom snaps to item top
            if (distanceBottomTop < this.tolerance) {
                guides.push(new Guide('y', 'bottom', itemTop, distanceBottomTop));
            }
            // selection top snaps to item bottom
            if (distanceTopBottom < this.tolerance) {
                guides.push(new Guide('y', 'top', itemBottom, distanceTopBottom));
            }
            // selection bottom snaps to item bottom
            if (distanceBottomBottom < this.tolerance) {
                guides.push(new Guide('y', 'bottom', itemBottom, distanceBottomBottom));
            }
        });

        return guides;
    }
}

namespace('Ofcs.Editor.Canvas');
window.Ofcs.Editor.Canvas.GuideManager = GuideManager;
